import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { TEConversionMembership } from '@x/common/tracking/event-models/conversion.events';
import { TrackingService } from '@x/common/tracking/tracking.service';
import { ChannelState } from '@x/ecommerce-shop/app/channel/state';
import { CheckoutUpdateOrderItem } from '@x/ecommerce-shop/app/checkout/state/checkout-order.actions';
import {
  AuthDialogResponse,
  AuthService,
} from '@x/ecommerce-shop/app/core/auth/services/auth.service';
import { AuthState } from '@x/ecommerce-shop/app/core/auth/state/auth.state';
import { TAvailabilityStatus } from '@x/ecommerce-shop/app/core/availability/availability';
import { OrderAddCartItem } from '@x/ecommerce-shop/app/core/order/state/shop-order.actions';
import { RegionOpenOverlay } from '@x/ecommerce-shop/app/core/region/state/region.actions';
import {
  MembershipDialogData,
  MembershipService,
} from '@x/ecommerce-shop/app/membership/services/membership.service';
import {
  IShopActivateMember,
  IShopCreateMember,
  IShopMemberDetail,
  IShopProductItem,
  ShopMemberService,
  TShopOrderItemContext,
} from '@x/ecommerce/shop-client';
import { MemberState } from '@x/schemas/ecommerce';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

interface BaseMembershipParams {
  membershipId: number;
  membershipName: string;
  activationUrl?: string | null;
}

interface MembershipDialogParams extends BaseMembershipParams {
  channelCode: string;
  userId: number;
  productVariantId: number;
  quantity: number;
  dialogData: MembershipDialogData;
  memberDetail: IShopMemberDetail | undefined | null;
}

interface CreateMembershipParams extends BaseMembershipParams {
  channelCode: string;
  userId: number;
}

interface ActivateMembershipParams extends BaseMembershipParams {
  memberDetail: IShopMemberDetail;
}

interface AddMembershipProductParams extends BaseMembershipParams {
  productVariantId: number;
  quantity: number;
}

interface HandleMembershipParams extends CreateMembershipParams, AddMembershipProductParams {
  memberDetail: IShopMemberDetail | undefined | null;
}

export type TAddToTarget = 'Checkout' | 'MiniCart';

export type TAddToCartOrigin =
  | 'taxon-listing'
  | 'product-listing'
  | 'search-listing'
  | 'landing-page'
  | 'product-detail'
  | 'shopping-bag'
  | 'add-to-bag-notification'
  | 'checkout-bag-step'
  | 'my-favourites-list'
  | 'home-page'
  | 'order-history';

@Injectable()
export class AddToCartService {
  constructor(
    private store: Store,
    private authService: AuthService,
    private router: Router,
    private membershipService: MembershipService,
    private shopMemberService: ShopMemberService,
    private trackingService: TrackingService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  addProduct(
    product: IShopProductItem,
    options?: {
      variantId?: number;
      quantity?: number;
      availabilityStatus?: TAvailabilityStatus;
      notify?: boolean;
      addToTarget?: TAddToTarget;
    },
    orderItemContext?: TShopOrderItemContext,
  ): Observable<any> {
    //if unavailable makes it here, return early
    if (options?.availabilityStatus === 'UNAVAILABLE') {
      return of(undefined);
    }

    const quantity = options?.quantity ?? 1;
    const variant = options?.variantId
      ? product.variants.find((v) => v.id === options.variantId)
      : product.variants[0];

    if (!variant) {
      throw new Error('Variant can not be resolved');
    }

    //limited availability
    if (options?.availabilityStatus === 'LIMITED') {
      this.store.dispatch(
        new RegionOpenOverlay({
          id: product.id,
          variantId: variant.id,
          quantity,
          variantAvailableRegions: variant.availableRegions,
        }),
      );

      return of(null);
    }

    // membership requirements
    const membershipRequirement = product.membershipRequirements[0];
    if (membershipRequirement) {
      return this.addMembershipProduct({
        productVariantId: variant.id,
        quantity,
        membershipId: membershipRequirement.id,
        membershipName: membershipRequirement.name,
        activationUrl: membershipRequirement.activationUrl,
      });
    }

    return this.addCartItem(
      variant.id,
      quantity,
      options?.notify,
      options?.addToTarget,
      orderItemContext,
    );
  }

  private addCartItem(
    productVariantId: number,
    quantity: number,
    notify: boolean = true,
    addToTarget: TAddToTarget = 'MiniCart',
    orderItemContext?: TShopOrderItemContext,
  ) {
    if (addToTarget === 'Checkout') {
      return this.store.dispatch(
        new CheckoutUpdateOrderItem({ productVariantId, quantity }, orderItemContext),
      );
    }
    return this.store.dispatch(
      new OrderAddCartItem({ productVariantId, quantity }, notify, orderItemContext),
    );
  }

  private openMembershipDialog(params: MembershipDialogParams) {
    const {
      channelCode,
      userId,
      membershipId,
      membershipName,
      memberDetail,
      productVariantId,
      quantity,
      activationUrl,
    } = params;

    return this.membershipService.openMembershipDialog(params.dialogData).pipe(
      switchMap((dialogResponse) => {
        let membershipState$: Observable<MemberState | undefined>;
        switch (dialogResponse) {
          case 'subscribe':
            membershipState$ = this.createMembership({
              channelCode,
              userId,
              membershipId,
              membershipName,
            }).pipe(map(({ state }) => state));
            break;

          case 'info':
            let infoUrl = `/m/${membershipId}`;
            if (activationUrl) {
              infoUrl = activationUrl.startsWith('/') ? activationUrl : `/${activationUrl}`;
            }

            this.router.navigate([`/${channelCode}${infoUrl}`]);
            membershipState$ = of(undefined);

            break;

          case 'activate':
            if (memberDetail) {
              membershipState$ = this.activateMembership({
                memberDetail,
                membershipName,
                membershipId,
              }).pipe(map(({ state }) => state));
              break;
            }

            membershipState$ = of(undefined);
            break;

          default:
            membershipState$ = of(undefined);
        }

        return membershipState$;
      }),
      switchMap((membershipState) => {
        if (membershipState && membershipState === 'ACTIVE') {
          return this.addCartItem(productVariantId, quantity);
        }

        return of(undefined);
      }),
    );
  }

  private handleMembership(params: HandleMembershipParams): Observable<any> {
    const {
      memberDetail,
      productVariantId,
      quantity,
      membershipName,
      channelCode,
      membershipId,
      userId,
      activationUrl,
    } = params;

    switch (memberDetail?.state) {
      case 'ACTIVE':
        return this.addCartItem(productVariantId, quantity);
      case 'INACTIVE':
        const dialogData: MembershipDialogData = {
          heading: `${membershipName}`,
          message:
            'Your membership is currently inactive. <br>Would you like to reactivate your membership?',
          actions: [
            {
              actionType: 'primary',
              response: 'info',
            },
          ],
        };
        return this.openMembershipDialog({
          channelCode,
          membershipId,
          membershipName,
          userId,
          productVariantId,
          quantity,
          dialogData,
          memberDetail,
          activationUrl,
        });
    }

    const dialogData: MembershipDialogData = {
      heading: `${membershipName}`,
      message: `This product is exclusive to the ${membershipName}. <br>Would you like to become a ${membershipName} member?`,
      actions: [
        {
          actionType: 'primary',
          response: 'info',
        },
      ],
    };
    return this.openMembershipDialog({
      channelCode,
      membershipId,
      membershipName,
      userId,
      productVariantId,
      quantity,
      dialogData,
      memberDetail,
      activationUrl,
    });
  }

  private openAuthMembershipDialog(membershipName: string): Observable<AuthDialogResponse> {
    return this.authService
      .openAuthDialog({
        heading: membershipName,
        message: `This product is only available to ${membershipName} members. <br> Already a member? Log in to purchase this product. `,
        login: true,
        register: true,
      })
      .pipe(
        tap((res) => {
          if (!res) return;

          this.router.navigate([`/${res}`], {
            queryParams: { redirect: this.router.url.split('?')[0] },
          });
        }),
      );
  }

  private createMembership(params: CreateMembershipParams): Observable<IShopCreateMember> {
    const { membershipId, userId, membershipName, channelCode } = params;
    return this.shopMemberService
      .createMember(
        {
          channelCode,
          userId,
          membershipId,
        },
        this.locale,
      )
      .pipe(tap(({ membership }) => membership && this.sendAnalyticsEvent(membership.name)));
  }

  private activateMembership(params: ActivateMembershipParams): Observable<IShopActivateMember> {
    const { membershipName, memberDetail } = params;
    return this.shopMemberService
      .activateMember({ memberId: memberDetail.id }, this.locale)
      .pipe(tap((subscription) => subscription && this.sendAnalyticsEvent(membershipName)));
  }

  private sendAnalyticsEvent(membershipName: string) {
    this.trackingService.sendEvent(new TEConversionMembership({ membershipName }));
  }

  private addMembershipProduct(params: AddMembershipProductParams): Observable<any> {
    const { membershipName, productVariantId, quantity, membershipId, activationUrl } = params;

    const user = this.store.selectSnapshot(AuthState.loggedInUser);
    const channelCode = this.store.selectSnapshot(ChannelState.activeChannel)?.code;

    if (!channelCode) return of(null);
    if (!user) return this.openAuthMembershipDialog(membershipName);

    const memberDetail$ = this.shopMemberService
      .fetchMemberDetail(user.id, channelCode, membershipId, this.locale)
      .pipe(shareReplay());

    return memberDetail$.pipe(
      switchMap((memberDetail: IShopMemberDetail | undefined | null) => {
        return this.handleMembership({
          memberDetail,
          membershipName,
          productVariantId,
          quantity,
          channelCode,
          membershipId,
          userId: user.id,
          activationUrl,
        });
      }),
      catchError((err) => {
        console.log('Could not add membership product', err);
        return of(undefined);
      }),
    );
  }
}
